// composables/layout.js
import {
    computed,
    reactive,
    ref,
    watch
} from 'vue';

const layoutConfig = reactive({
    preset: 'Aura',
    primary: 'indigo',
    surface: 'slate',
    darkTheme: false,
    menuMode: 'static',
    menuTheme: 'light',
    topbarTheme: 'indigo',
    menuProfilePosition: 'end'
});

const layoutState = reactive({
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    sidebarActive: false,
    anchored: false,
    activeMenuItem: null,
    overlaySubmenuActive: false,
    menuProfileActive: false
});

const outsideClickListener = ref(null);

export function useLayout() {
    const setPrimary = (value) => {
        layoutConfig.primary = value;
        saveLayoutConfig();
    };

    const setSurface = (value) => {
        layoutConfig.surface = value;
        saveLayoutConfig();
    };

    const setPreset = (value) => {
        layoutConfig.preset = value;
        saveLayoutConfig();
    };

    const setMenuMode = (mode) => {
        layoutConfig.menuMode = mode;
        if (mode === 'static') {
            layoutState.staticMenuDesktopInactive = false;
        }
        saveLayoutConfig();
    };

    const showConfigSidebar = () => {
        layoutState.configSidebarVisible = true;
    };

    const showSidebar = () => {
        layoutState.rightMenuActive = true;
    };

    const onMenuProfileToggle = () => {
        layoutState.menuProfileActive = !layoutState.menuProfileActive;
    };

    const setMenuTheme = (theme) => {
        layoutConfig.menuTheme = theme;
        saveLayoutConfig();
    };

    const setTopbarTheme = (theme) => {
        layoutConfig.topbarTheme = theme;
        saveLayoutConfig();
    };

    const setProfilePosition = (value) => {
        layoutConfig.menuProfilePosition = value;
        saveLayoutConfig();
    };

    const setActiveMenuItem = (item) => {
        layoutState.activeMenuItem = item.value || item;
    };

    const setMenuStates = (value) => {
        layoutState.overlaySubmenuActive = value;
        layoutState.menuHoverActive = value;
    };

    const setStaticMenuMobile = () => {
        layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
    };

    const watchSidebarActive = () => {
        watch(isSidebarActive, (newVal) => {
            if (newVal) {
                bindOutsideClickListener();
            } else {
                unbindOutsideClickListener();
            }
        });
    };

    const onMenuToggle = () => {
        if (layoutConfig.menuMode === 'overlay') {
            layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
        }

        if (window.innerWidth > 991) {
            layoutState.staticMenuDesktopInactive = !layoutState.staticMenuDesktopInactive;
        } else {
            layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
        }
    };

    const onTopbarMenuToggle = () => {
        layoutState.topbarMenuActive = !layoutState.topbarMenuActive;
    };

    const openRightSidebar = () => {
        layoutState.rightMenuActive = true;
    };

    const onProfileSidebarToggle = () => {
        layoutState.rightMenuActive = !layoutState.rightMenuActive;
    };

    const onConfigSidebarToggle = () => {
        if (isSidebarActive.value) {
            resetMenu();
            unbindOutsideClickListener();
        }
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
    };

    const onSidebarToggle = (value) => {
        layoutState.sidebarActive = value;
    };

    const onAnchorToggle = () => {
        layoutState.anchored = !layoutState.anchored;
    };

    const toggleDarkMode = () => {
        if (!document.startViewTransition) {
            executeDarkModeToggle();
            return;
        }
        document.startViewTransition(() => executeDarkModeToggle());
    };

    const executeDarkModeToggle = () => {
        layoutConfig.darkTheme = !layoutConfig.darkTheme;
        layoutConfig.menuTheme = isDarkTheme.value ? 'dark' : 'light';
        document.documentElement.classList.toggle('app-dark');
        saveLayoutConfig();
    };

    const saveLayoutConfig = () => {
        localStorage.setItem('layoutConfig', JSON.stringify(layoutConfig));
    };

    const loadLayoutConfig = () => {
        const storedConfig = localStorage.getItem('layoutConfig');
        if (storedConfig) {
            const parsedConfig = JSON.parse(storedConfig);
            Object.assign(layoutConfig, parsedConfig);
        }
    };

    // Initialize layout
    loadLayoutConfig();

    const bindOutsideClickListener = () => {
        if (!outsideClickListener.value) {
            outsideClickListener.value = (event) => {
                if (isOutsideClicked(event)) {
                    resetMenu();
                }
            };
            document.addEventListener('click', outsideClickListener.value);
        }
    };

    const unbindOutsideClickListener = () => {
        if (outsideClickListener.value) {
            document.removeEventListener('click', outsideClickListener.value);
            outsideClickListener.value = null;
        }
    };

    const isOutsideClicked = (event) => {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarButtonEl = document.querySelector('.layout-menu-button');

        const sidebarContains = sidebarEl && (sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target));
        const topbarContains = topbarButtonEl && (topbarButtonEl.isSameNode(event.target) || topbarButtonEl.contains(event.target));

        return !(sidebarContains || topbarContains);
    };

    const resetMenu = () => {
        layoutState.overlayMenuActive = false;
        layoutState.overlaySubmenuActive = false;
        layoutState.staticMenuMobileActive = false;
        layoutState.menuHoverActive = false;
        layoutState.configSidebarVisible = false;
    };

    const isSidebarActive = computed(() => layoutState.overlayMenuActive || layoutState.staticMenuMobileActive || layoutState.overlaySubmenuActive);
    const isDesktop = computed(() => window.innerWidth > 991);
    const isSlim = computed(() => layoutConfig.menuMode === 'slim');
    const isSlimPlus = computed(() => layoutConfig.menuMode === 'slim-plus');
    const isHorizontal = computed(() => layoutConfig.menuMode === 'horizontal');
    const isOverlay = computed(() => layoutConfig.menuMode === 'overlay');
    const isDarkTheme = computed(() => layoutConfig.darkTheme);
    const getPrimary = computed(() => layoutConfig.primary);
    const getSurface = computed(() => layoutConfig.surface);

    return {
        layoutConfig,
        layoutState,
        getPrimary,
        getSurface,
        isDarkTheme,
        setPrimary,
        setSurface,
        setPreset,
        setMenuMode,
        setProfilePosition,
        setMenuTheme,
        setTopbarTheme,
        onMenuProfileToggle,
        toggleDarkMode,
        onMenuToggle,
        onTopbarMenuToggle,
        openRightSidebar,
        onProfileSidebarToggle,
        setMenuStates,
        setStaticMenuMobile,
        watchSidebarActive,
        isSidebarActive,
        setActiveMenuItem,
        onConfigSidebarToggle,
        onSidebarToggle,
        onAnchorToggle,
        isSlim,
        isSlimPlus,
        isHorizontal,
        isDesktop,
        isOverlay,
        showConfigSidebar,
        showSidebar,
        unbindOutsideClickListener
    };
}